/**
 * connectionsApi.ts — API client per la Cerchia Bikers Preferiti
 *
 * Tutte le chiamate autenticate tramite boundApiFetch.
 */

import { boundApiFetch } from "@/api/core/boundApiFetch";

// ─── Tipi ────────────────────────────────────────────────────────────────────

export interface BikerConnection {
  connection_id: string;
  biker_id: string;
  biker_name: string;
  biker_avatar: string | null;
  biker_city: string | null;
  status: "accepted" | "pending" | "rejected" | "blocked" | "cancelled";
  requested_at: string;
  responded_at: string | null;
  // Contatto rapido (solo se phone_visible = true)
  phone: string | null;
  whatsapp_opt_in: boolean;
  // Presenza live
  presence_status: "online" | "riding" | "offline" | null;
  presence_last_seen: string | null;
  // Garage marketplace
  has_bikes_for_sale: boolean;
  bikes_for_sale_count: number;
  // Statistiche condivise
  shared_routes_count: number;
  shared_events_count: number;
  friendship_days: number;
}

export interface PendingRequest {
  id: string;
  biker_id: string;
  biker_name: string;
  biker_avatar: string | null;
  status: string;
  requested_at: string;
}

export interface ConnectionsListResponse {
  connections: BikerConnection[];
  total: number;
  meta: {
    tier: string;
    is_trial: boolean;
    trial_days_remaining: number;
    // FEAT CERCHIA-FREE (18 Set 2026)
    is_premium: boolean;
    free_limit: number;
    accepted_count: number;
  };
}

export interface PendingRequestsResponse {
  sent: PendingRequest[];
  received: PendingRequest[];
}

export interface ConnectionStatusResponse {
  status: "self" | "none" | "accepted" | "pending" | "rejected" | "blocked" | "cancelled";
  connection_id?: string;
  is_requester?: boolean;
  requested_at?: string;
  responded_at?: string;
}

export interface UnreadCountResponse {
  pending_count: number;
}

// ─── API calls ───────────────────────────────────────────────────────────────

/** Lista cerchia (accepted). Aperta a tutti — i FREE vedono max 3 biker. */
export function fetchConnections(limit = 50): Promise<ConnectionsListResponse> {
  return boundApiFetch<ConnectionsListResponse>(`/api/connections?limit=${limit}`);
}

/** Richieste inviate e ricevute (pending). */
export function fetchPendingRequests(): Promise<PendingRequestsResponse> {
  return boundApiFetch<PendingRequestsResponse>("/api/connections/pending");
}

/** Stato connessione con un biker specifico. */
export function fetchConnectionStatus(bikerId: string): Promise<ConnectionStatusResponse> {
  return boundApiFetch<ConnectionStatusResponse>(`/api/connections/${bikerId}/status`);
}

/** Conteggio richieste in attesa (per badge navbar). */
export function fetchUnreadCount(): Promise<UnreadCountResponse> {
  return boundApiFetch<UnreadCountResponse>("/api/connections/unread-count");
}

/** Invia richiesta di connessione. */
export function sendConnectionRequest(targetId: string): Promise<{
  success: boolean;
  connection: { id: string; requested_at: string };
  message: string;
}> {
  return boundApiFetch("/api/connections/request", {
    method: "POST",
    body: JSON.stringify({ targetId }),
  });
}

/** Accetta richiesta di connessione. */
export function acceptConnection(connectionId: string): Promise<{
  success: boolean;
  connection: any;
}> {
  return boundApiFetch(`/api/connections/${connectionId}/accept`, {
    method: "PATCH",
  });
}

/** Rifiuta richiesta di connessione. */
export function rejectConnection(connectionId: string): Promise<{
  success: boolean;
  connection: any;
}> {
  return boundApiFetch(`/api/connections/${connectionId}/reject`, {
    method: "PATCH",
  });
}

/** Rimuovi/cancella connessione. */
export function removeConnection(connectionId: string): Promise<{
  success: boolean;
  connection: any;
}> {
  return boundApiFetch(`/api/connections/${connectionId}`, {
    method: "DELETE",
  });
}
